/**
 * The wire half of the e-mail + code login (wave 9, contract #2.5/#2.6).
 *
 * `./email-code-model` owns the decisions and is what the `unit` project tests;
 * this file only carries bytes. It cannot go through `apiRequest` for one reason:
 * the 429 of #2.5 carries its cooldown in the `Retry-After` HEADER, and a helper
 * that throws an `ApiError` built from the body alone would drop it. So the two
 * hops fetch directly — the same licence `./exports.ts` takes for its file
 * download — and keep every other house rule: the relative `/api` prefix, the
 * envelope error parsing, and the fact that a 401 here is DATA (a wrong code),
 * never a bounce to OAuth.
 */

import { API_BASE_URL, ApiError, auth } from './client';
import { parseErrorBody, statusCodeToCode } from './envelope';
import {
  EMAIL_CODE_REQUEST_PATH,
  EMAIL_CODE_VERIFY_PATH,
  cooldownFromRejection,
  cooldownFromResponse,
  isCodeShape,
  validateEmail,
  type CooldownSeconds,
  type EmailCodeRequestInput,
  type EmailCodeVerifyInput,
} from './email-code-model';
import { landingAfterLogin } from '../auth/session-provider';

export {
  CODE_SHAPE_ERROR,
  EMAIL_CODE_REQUEST_PATH,
  EMAIL_CODE_VERIFY_PATH,
  countdownLabel,
  cooldownFromRejection,
  cooldownFromResponse,
  emailCodeErrorCopy,
  isCodeShape,
  nextCountdown,
  validateEmail,
} from './email-code-model';
export type { CooldownSeconds, EmailCodeRequestInput, EmailCodeVerifyInput } from './email-code-model';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** #2.5 — «Получить код». Resolves with the cooldown the countdown draws from. */
export async function requestEmailCode(input: EmailCodeRequestInput): Promise<CooldownSeconds> {
  const shapeError = validateEmail(input.email);
  if (shapeError !== null) throw new ApiError(400, 'POST', EMAIL_CODE_REQUEST_PATH, 'VALIDATION', shapeError);

  const response = await fetch(`${API_BASE_URL}${EMAIL_CODE_REQUEST_PATH}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ email: input.email.trim() }),
  });

  if (!response.ok) {
    const parsed = await parse(response);
    // The 429's cooldown rides in `Retry-After`; `ApiError.meta` already carries
    // the structured twin, so the login page reads either.
    const cooldown = cooldownFromRejection(parsed.meta, response.headers.get('Retry-After'));
    throw new ApiError(
      response.status,
      'POST',
      EMAIL_CODE_REQUEST_PATH,
      parsed.code,
      parsed.message,
      cooldown ? { retryAfterSeconds: cooldown.resendAfterSeconds } : parsed.meta,
    );
  }

  const cooldown = cooldownFromResponse(await response.json());
  // A 200 without a cooldown is a shape we did not agree on; the caller treats
  // «no countdown» as «the link is already enabled» rather than crashing.
  return cooldown ?? { resendAfterSeconds: 0 };
}

export interface EmailCodeVerifyResult {
  /**
   * The contract's own `redirect` (`/`) — INFORMATIONAL: the SPA has never
   * navigated by it. T.Z85 decides the landing by role (see
   * {@link landingAfterVerify}); the field stays pinned by the e2e-auth suite
   * because it is the wire shape of #2.6.
   */
  redirect: string;
}

/** #2.6 — «Войти». The session cookie is set by the answer itself. */
export async function verifyEmailCode(input: EmailCodeVerifyInput): Promise<EmailCodeVerifyResult> {
  const emailError = validateEmail(input.email);
  if (emailError !== null) throw new ApiError(400, 'POST', EMAIL_CODE_VERIFY_PATH, 'VALIDATION', emailError);
  if (!isCodeShape(input.code)) {
    throw new ApiError(400, 'POST', EMAIL_CODE_VERIFY_PATH, 'VALIDATION', 'Код состоит из 6 цифр.');
  }

  const response = await fetch(`${API_BASE_URL}${EMAIL_CODE_VERIFY_PATH}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ email: input.email.trim(), code: input.code.trim() }),
  });

  if (!response.ok) {
    const parsed = await parse(response);
    throw new ApiError(response.status, 'POST', EMAIL_CODE_VERIFY_PATH, parsed.code, parsed.message, parsed.meta);
  }

  const body = (await response.json()) as { data?: { redirect?: unknown } };
  const redirect = typeof body.data?.redirect === 'string' ? body.data.redirect : '';
  return { redirect };
}

/**
 * T.Z85 — where the SPA goes after a successful #2.6: BY ROLE, not by provider.
 *
 * The verify itself carries no role (the contract answers `redirect: '/'`, see
 * {@link EmailCodeVerifyResult}), so the session the verify just created is read
 * once — the same `GET /auth/me` the shell boots on — and
 * `landingAfterLogin` (the Z60 predicate in `../auth/session-provider`) turns
 * its role into the start page: a КУРАТОР-role session → `/cabinet`, a staff
 * session → `/`.
 *
 * A failed read cannot undo the session, and guessing a role here would be a
 * second landing definition: the fallback is `/`, where the index route applies
 * the SAME predicate once the session resolves (so even this fallback lands a
 * КУРАТОР correctly).
 */
export async function landingAfterVerify(): Promise<string> {
  try {
    const me = await auth.me();
    return landingAfterLogin(me.data.role ?? me.data.user?.role);
  } catch {
    return landingAfterLogin(undefined);
  }
}

/** Error parsing for a hop this module fetches itself. */
async function parse(response: Response): Promise<{ code: ReturnType<typeof statusCodeToCode>; message: string; meta?: Record<string, string | number | boolean> }> {
  try {
    return parseErrorBody(await response.json(), response.status);
  } catch {
    return {
      code: statusCodeToCode(response.status),
      message: response.statusText || 'неизвестная ошибка',
    };
  }
}
